import { useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { browserHistory } from 'react-router';
import type { VoidFunctionComponent } from 'react';

import useCreateRelease from '../../hooks/useCreateRelease';
import { useFetchReleaseWithCallback } from '../../hooks/useFetchRelease';
import { useFetchMinimalProjectsAndUpdateState } from '../../hooks/useMinimalProjects';
import useReleaseGroupState from '../../hooks/useReleaseGroupState';
import ErrorBanner from '../../shared/ErrorBanner';
import ProjectsPage from '../../shared/ProjectsPage/ProjectsPage';
import type { NewReleaseProject } from '../../../../../../../shared/releaseGroups';
import type { ProjectGroupRelease, RouterProps } from '../../../../../../types';

import styles from '../../shared/ReleaseGroupsRevamp.module.less';

type Props = RouterProps<{ projectGroupId: string; releaseId: string }> & {
  //  This comes from the ProjectGroupContainer + Redux.
  //  We need this because after cloning a release, we navigate back to the releases page,
  //  which will have stale data/won't include the newly cloned release, so we need to refetch it.
  //  Later we should replace this with a hook.
  fetchProjectGroup: (projectGroupId: number) => Promise<void>;
};

const CloneReleaseContainer: VoidFunctionComponent<Props> = ({ params, fetchProjectGroup }) => {
  const groupId = params.projectGroupId;
  const releaseId = params.releaseId;

  const state = useReleaseGroupState();

  const { onAddAllProjects, onRemoveAllProjects } = useFetchMinimalProjectsAndUpdateState(
    state.addMinimalProjects,
    state.removeProjects,
    state.filters,
  );

  //  This callback is called after (a non-null) release is fetched.
  const handleFetchRelease = (release: ProjectGroupRelease) => {
    state.setVersion(release.title);

    state.addReleaseGroupProjects(release.projects);
    state.setFilters({
      locators: release.projects.map((project) => project.projectId),
    });
  };

  const { release } = useFetchReleaseWithCallback(groupId, releaseId, handleFetchRelease);

  const {
    onCreateRelease,
    errors,
    onResetErrors,
    isCreatingRelease,
  } = useCreateRelease(fetchProjectGroup);

  const onSave = useCallback(() => {
    const projects: NewReleaseProject[] = [];
    Object.values(state.addedProjects).forEach((project) => {
      // If this project has no revision selected, skip it.
      if (!project.revision) {
        return;
      }

      const newReleaseProject: NewReleaseProject = {
        projectId: project.locator,
        branch: project.branch,
        revisionId: project.revision.locator,
      };
      projects.push(newReleaseProject);
    });
    return onCreateRelease(groupId, {
      title: state.version,
      projects,
    });
  }, [groupId, state.addedProjects, state.version, onCreateRelease]);

  const onCancel = () => browserHistory.push(`/projects/group/${groupId}/releases`);

  return (
    <>
      <Helmet
        title={release?.title}
        titleTemplate="%s - Clone Release | FOSSA"
        defaultTitle="Clone Release | FOSSA"
      />
      {errors && (
        <div className={styles.banner}>
          <ErrorBanner
            title="An error occurred while cloning this release."
            body={errors.join('. ')}
            onDismiss={onResetErrors}
          />
        </div>
      )}
      <ProjectsPage>
        <ProjectsPage.Header
          pageTitle={`Clone ${release?.title || 'release'}`}
          saveButtonText="Clone"
          isLoading={isCreatingRelease}
          validationErrors={state.validate()}
          onCancel={onCancel}
          onSave={onSave}
        />
        <ProjectsPage.Details
          title="Details"
          description="Update the release version"
          name={state.name}
          version={state.version}
          setName={state.setName}
          setVersion={state.setVersion}
        />
        <ProjectsPage.Projects
          projects={state.projects}
          projectCount={state.projectCount}
          areProjectsLoading={state.areProjectsLoading}
          addedProjects={state.addedProjects}
          projectErrors={state.projectErrors}
          onUpdateAddedProject={state.updateAddedProject}
          onAddProject={state.addProjects}
          onRemoveProject={state.removeProjects}
          onAddAllProjects={onAddAllProjects}
          onRemoveAllProjects={onRemoveAllProjects}
          shouldShowOnlyAddedProjects={state.shouldShowOnlyAddedProjects}
          setShouldShowOnlyAddedProjects={state.setShouldShowOnlyAddedProjects}
          filters={state.filters}
          setFilters={state.setFilters}
          onSelectLatestRevisionForSelectedProjects={state.selectLatestRevisionForSelectedProjects}
          sort={state.sort}
          setSort={state.setSort}
          page={state.page}
          setPage={state.setPage}
          count={state.count}
          setCount={state.setCount}
        />
        <ProjectsPage.Footer
          saveButtonText="Clone"
          validationErrors={state.validate()}
          onCancel={onCancel}
          onSave={onSave}
        />
      </ProjectsPage>
    </>
  );
};

export default CloneReleaseContainer;
